import type { ArticleKind, OAuthProvider } from '@nagoya-cat/shared';

/** 簡易 API client：統一 `{ data } / { error }` 格式、帶上 cookie。 */
export interface Me {
  userId: string;
  displayName: string | null;
  email: string | null;
  roles: string[];
  permissions: string[];
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const body = (await res.json()) as { data?: T; error?: string };
  if (!res.ok || body.error) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body.data as T;
}

export interface ArticleDetail {
  article: ArticleRow & {
    bodyJson: string | null;
    bodyHtmlLegacy: string | null;
    excerpt: string | null;
    coverMediaId: string | null;
    parentId: string | null;
    menuOrder: number | null;
  };
  coverUrl: string | null;
  seo: Record<string, unknown> | null;
  relatedIds: string[];
  categoryIds: string[];
  tagIds: string[];
}

export interface SavePayload {
  patch?: Record<string, unknown>;
  seo?: Record<string, unknown>;
  relatedIds?: string[];
  categoryIds?: string[];
}

/** 比賽存檔（read-only）：名次榜。 */
export interface ContestSummary {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  entryCount: number;
  dateFinish: number | null;
}

export interface ContestStanding {
  id: string;
  name: string;
  place: number | null;
  votesCount: number;
  votesAverage: number;
  likes: number;
  views: number;
  coverUrl: string | null;
}

export const api = {
  me: () => request<Me | null>('/auth/me'),
  logout: () => request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  identities: () => request<{ provider: OAuthProvider; email: string | null }[]>('/auth/identities'),
  unlinkProvider: (p: OAuthProvider) => request<{ ok: boolean }>(`/auth/${p}/unlink`, { method: 'POST' }),
  listArticles: (params: Record<string, string>) =>
    request<{ items: ArticleRow[]; total: number }>(
      `/articles?${new URLSearchParams(params).toString()}`,
    ),
  getArticle: (id: string) => request<ArticleDetail>(`/articles/${id}`),
  articlesByIds: (ids: string[]) =>
    request<{ items: ArticleRow[] }>(`/articles?ids=${ids.join(',')}`),
  createArticle: (title: string, kind: ArticleKind) =>
    request<{ id: string }>('/articles', { method: 'POST', body: JSON.stringify({ title, kind }) }),
  saveArticle: (id: string, payload: SavePayload) =>
    request<{ ok: boolean }>(`/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  publishArticle: (id: string) => request<{ ok: boolean }>(`/articles/${id}/publish`, { method: 'POST' }),
  unpublishArticle: (id: string) =>
    request<{ ok: boolean }>(`/articles/${id}/unpublish`, { method: 'POST' }),
  previewToken: (id: string) =>
    request<{ url: string }>(`/articles/${id}/preview-token`, { method: 'POST' }),
  categories: () => request<{ id: string; name: string; slug: string }[]>('/categories'),
  users: () => request<{ id: string; displayName: string }[]>('/users'),
  usersManage: () =>
    request<
      {
        id: string;
        displayName: string;
        email: string | null;
        status: string;
        providers: string | null;
        roles: string | null;
      }[]
    >('/users/manage'),
  setUserRole: (id: string, roleId: string) =>
    request<{ ok: boolean }>(`/users/${id}/role`, { method: 'POST', body: JSON.stringify({ roleId }) }),
  mediaList: () =>
    request<{ items: { id: string; url: string; mime: string | null; title: string | null; createdAt: number }[] }>(
      '/media/list',
    ),
  invite: (email: string, roleId: string, send: boolean) =>
    request<{ acceptUrl: string; emailed: boolean }>('/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, roleId, send }),
    }),
  contests: () => request<{ items: ContestSummary[] }>('/contests'),
  contestStandings: (id: string) =>
    request<{ contest: ContestSummary; items: ContestStanding[] }>(`/contests/${id}/standings`),
  uploadImage: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/media', { method: 'POST', credentials: 'include', body: fd });
    const body = (await res.json()) as { data?: { id: string; url: string }; error?: string };
    if (!res.ok || body.error) throw new Error(body.error ?? 'upload failed');
    return body.data!;
  },
};

export interface ArticleRow {
  id: string;
  kind: ArticleKind;
  title: string;
  slug: string;
  status: string;
  authorId?: string | null;
  authorName?: string | null;
  coverUrl?: string | null;
  categoryNames?: string | null;
  publishedAt: number | null;
  scheduledAt?: number | null;
  updatedAt: number;
}

/** 觸發 provider 登入（整頁導向）。 */
export function loginWith(provider: OAuthProvider): void {
  window.location.href = `/api/auth/${provider}/login`;
}

/** 連結其他登入方式到目前帳號（整頁導向）。 */
export function linkWith(provider: OAuthProvider): void {
  window.location.href = `/api/auth/${provider}/link`;
}
